import { motion } from "framer-motion";
import { Microscope, FileCheck2, Snowflake, ScanLine } from "lucide-react";
import Reveal from "./Reveal";

const MICROSCOPE_IMG = "https://images.unsplash.com/photo-1614308459036-779d0dfe51ff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NxAzMzV8MHwxfHNlYXJjaHwzfHxiaW90ZWNoJTIwbGFib3JhdG9yeSUyMHJlc2VhcmNoJTIwc2NpZW50aXN0JTIwbWljcm9zY29wZSUyMG1vbGVjdWxlJTIwcmVzZWFyY2glMjBsYWJ8ZW58MHx8fHwxNzkxNzA3Njk5fDA&ixlib=rb-4.1.0&q=85";
const COLDCHAIN_IMG = "https://images.unsplash.com/photo-1684695749267-233af13276d0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxjb2xkJTIwY2hhaW4lMjBwaGFybWFjZXV0aWNhbCUyMHNoaXBwaW5nJTIwY29udGFpbmVyJTIwd2FyZWhvdXNlfGVufDB8fHx8MTc5MTcwNzcwOHww&ixlib=rb-4.1.0&q=85";

const POINTS = [
    {
        icon: Microscope,
        title: "Independent HPLC analysis",
        body: "Every batch is quantified by reversed-phase HPLC at an independent Australian analytical laboratory before release.",
    },
    {
        icon: ScanLine,
        title: "Mass spectrometry confirmation",
        body: "Molecular identity is confirmed by ESI-MS against the theoretical monoisotopic mass of each sequence.",
    },
    {
        icon: FileCheck2,
        title: "Batch-isolated COAs",
        body: "Certificates of Analysis are issued per batch number — never generic, never recycled across production runs.",
    },
    {
        icon: Snowflake,
        title: "Monitored cold chain",
        body: "Lyophilised compounds ship at -20°C with continuous temperature logging from dispatch to delivery.",
    },
];

export default function Quality() {
    return (
        <section id="quality" data-testid="quality-section" className="py-16 md:py-24 bg-slate-900 text-slate-100 overflow-hidden">
            <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
                <Reveal>
                    <div className="font-jbm text-xs uppercase tracking-[0.25em] text-cyan-400 mb-3">02 — Quality & purity</div>
                    <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight max-w-2xl">
                        Analytical rigour you can audit, batch by batch
                    </h2>
                </Reveal>

                <div className="mt-14 grid lg:grid-cols-12 gap-10 items-start">
                    <div className="lg:col-span-5 space-y-6">
                        <Reveal delay={0.1}>
                            <div className="relative rounded-3xl overflow-hidden border border-slate-700/60 group">
                                <img
                                    src={MICROSCOPE_IMG}
                                    alt="Laboratory microscope analysis"
                                    className="w-full aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent" />
                                <div className="absolute bottom-5 left-5 font-jbm text-xs text-cyan-300 tracking-widest uppercase">Third-party analytics · Melbourne</div>
                            </div>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <div className="relative rounded-3xl overflow-hidden border border-slate-700/60 group">
                                <img
                                    src={COLDCHAIN_IMG}
                                    alt="Cold-chain storage facility"
                                    className="w-full aspect-[16/9] object-cover transition-transform duration-700 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent" />
                                <div className="absolute bottom-5 left-5 font-jbm text-xs text-cyan-300 tracking-widest uppercase">-20°C validated storage</div>
                            </div>
                        </Reveal>
                    </div>

                    <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
                        {POINTS.map((p, i) => (
                            <Reveal key={p.title} delay={0.1 + i * 0.08}>
                                <motion.div
                                    whileHover={{ y: -6 }}
                                    transition={{ type: "spring", stiffness: 300, damping: 22 }}
                                    className="h-full bg-slate-800/60 border border-slate-700/60 rounded-2xl p-6 hover:border-cyan-500/50 transition-colors duration-300"
                                >
                                    <div className="w-11 h-11 rounded-full bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center mb-5">
                                        <p.icon className="w-5 h-5 text-cyan-400" />
                                    </div>
                                    <h3 className="font-display text-lg font-semibold tracking-tight">{p.title}</h3>
                                    <p className="text-sm leading-relaxed text-slate-400 mt-2">{p.body}</p>
                                </motion.div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
